import React, { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';

import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import FormHeader from 'prequalificationV2/components/FormHeader';
import Loader from 'components/Loader';
import { ButtonType } from 'enums/ButtonType';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { getErrorMessageForRequiredFields, MONETARY_PREFIX, removeNonDigits } from 'utils/stringUtils';
import NumberInput from 'components/Inputs/NumberInput';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import { preQualificationApi } from 'prequalificationV2/api/PreQualification/PreQualificationApi';
import { PreQualificationStep } from 'enums/PreQualificationStep';
import {
  PropertyInformationFieldName,
  PropertyInformationInputLabel,
} from 'prequalificationV2/pages/PreQualification/steps/PropertyInformation/propertyInformation.enums';
import { PropertyInformationFormProps } from 'prequalificationV2/pages/PreQualification/steps/PropertyInformation/propertyInformation.types';
import { setPreQualificationStep } from 'prequalificationV2/actions';
import { marketingApi } from 'api/MarketingApi';
import { PrequalificationVersion } from 'enums/ApplicationsVariables';
import { ConceptualHqEvent } from 'enums/ConceptualHqEvent';
import { getConceptualDeviceId } from 'prequalificationV2/routes/routes.utils';
import { useNavigateAfterUnderwriting } from 'prequalificationV2/hooks/useNavigateAfterUnderwriting';

import styles from './PropertyInformation.module.scss';

const PropertyInformation = () => {
  const dispatch = useAppDispatch();
  const { navigateToNextStep } = useNavigateAfterUnderwriting();

  const { application } = useAppSelector((store) => store);
  const { borrowerId } = useAppSelector((state) => state.borrower);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isMonthlyMortgageRequired, setIsMonthlyMortgageRequired] = useState<boolean>(false);

  const { register, watch, handleSubmit, setValue, formState, clearErrors } = useForm<PropertyInformationFormProps>({
    defaultValues: {
      [PropertyInformationFieldName.MonthlyMortgage]: '',
    },
  });

  const watcher = watch();

  const distributeOffersAndNavigate = async () => {
    const { nextStep } = await preQualificationApi.runProductAndOffersDistribution(application?.applicationId!);

    await navigateToNextStep(nextStep);
  };

  useEffect(() => {
    const getPropertyInformation = async () => {
      try {
        setIsLoading(true);

        const { isMonthlyMortgageRequired: isRequired } = await preQualificationApi.getPropertyInformation(
          application?.applicationId!,
        );

        if (!isRequired) {
          await distributeOffersAndNavigate();

          return;
        }

        setIsMonthlyMortgageRequired(isRequired);
        setIsLoading(false);
      } catch (err) {
        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
        setIsLoading(false);
      }
    };

    getPropertyInformation();

    const track = async () => {
      const deviceId = await getConceptualDeviceId();

      if (!deviceId) {
        return;
      }

      marketingApi
        .trackConceptualHqEvent({
          event: ConceptualHqEvent.Property,
          prequalificationVersion: PrequalificationVersion.Second,
          borrowerId: borrowerId,
          deviceId: deviceId,
        })
        .catch(() => {});
    };

    track();
  }, []);

  useEffect(() => {
    if (isMonthlyMortgageRequired) {
      register(PropertyInformationFieldName.MonthlyMortgage, {
        required: getErrorMessageForRequiredFields(PropertyInformationInputLabel.MonthlyMortgage),
      });
    }
  }, [isMonthlyMortgageRequired]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      setIsLoading(true);

      await preQualificationApi.updatePropertyInformation({
        applicationId: application?.applicationId!,
        monthlyMortgage: +removeNonDigits(data[PropertyInformationFieldName.MonthlyMortgage]),
      });

      await distributeOffersAndNavigate();
    } catch (err) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  });

  const goPreviousStep = () => {
    dispatch(setPreQualificationStep(PreQualificationStep.LoanInformation));
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <FormHeader title="Property" percent={75} step={4} totalSteps={4} />
          <FormBody onSubmit={onSubmit}>
            <div className={styles.mortgageContainer}>
              <NumberInput
                label={PropertyInformationInputLabel.MonthlyMortgage}
                prefix={MONETARY_PREFIX}
                placeholder={MONETARY_PREFIX}
                thousandSeparator
                name={PropertyInformationFieldName.MonthlyMortgage}
                onChange={(event) => {
                  clearErrors(PropertyInformationFieldName.MonthlyMortgage);

                  setValue(PropertyInformationFieldName.MonthlyMortgage, event.target.value);
                }}
                errorMessage={formState.errors[PropertyInformationFieldName.MonthlyMortgage]?.message}
                value={watcher[PropertyInformationFieldName.MonthlyMortgage]}
                allowNegative={false}
              />
            </div>
          </FormBody>
          <div className={styles.buttonControlContainer}>
            <Button buttonType={ButtonType.Secondary} withArrow onClick={() => goPreviousStep()}>
              Back
            </Button>
            <Button type="submit" form="form" withArrow>
              Continue
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default PropertyInformation;
